import React, { useEffect, useState } from "react";
import "./AdminDashboard.css";
import "./AddEmployee.css";
import fetchBase from "../utils/fetchBase";

const AddEmployee = ({ autofillName = "", countRemaining = 0, onSuccess }) => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        confirmPhone: "",
        email: "",
        confirmEmail: "",
        position: "",
    });
    // Use state lines
    const [customPosition, setCustomPosition] = useState("");
    const [success, setSuccess] = useState(false);
    const [phoneMatch, setPhoneMatch] = useState(true);
    const [emailMatch, setEmailMatch] = useState(true);
    const [emailValid, setEmailValid] = useState(true);

    const positions = ["Caretaker", "Driver", "Nurse", "Other (enter manually)"];

    // Autofill firstName and lastName from autofillName (e.g., "Jane Doe")
    useEffect(() => {
        if (autofillName) {
            const parts = autofillName.trim().split(" ");
            setFormData(prev => ({
                ...prev,
                firstName: parts[0] || "",
                lastName: parts.slice(1).join(" ") || ""
            }));
        }
    }, [autofillName]);

    const handleChange = (e) => {
        let { name, value } = e.target;

        // --- Auto-format phone numbers ---
        if (name === "phone" || name === "confirmPhone") {
            // remove all non-digits
            value = value.replace(/\D/g, "");

            // limit to 10 digits
            if (value.length > 10) value = value.slice(0, 10);

            // apply formatting: (123) 456-7890
            if (value.length >= 7) {
                value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
            } else if (value.length >= 4) {
                value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
            } else if (value.length >= 1) {
                value = `(${value}`;
            }
        }

        setFormData((prev) => {
            const updated = { ...prev, [name]: value };

            // Phone match check
            if (name === "phone" || name === "confirmPhone") {
                setPhoneMatch(updated.phone === updated.confirmPhone);
            }

            // Email match + format check (NO visible indicator for format)
            if (name === "email" || name === "confirmEmail") {
                setEmailMatch(updated.email === updated.confirmEmail);

                // Background email format validation (silent)
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                setEmailValid(emailPattern.test(updated.email));
            }

            return updated;
        });
    };

    const handleAddEmployee = async () => {
        const finalPosition =
            formData.position === "Other (enter manually)"
                ? customPosition
                : formData.position;


        // Makes a post request to link, and passes JSON data for the employees
        // This endpoint is protected by SpringSecurity and it needs an authorization header with a vaild backend URL mapping

        try {
            const response = await fetch(`${fetchBase}/api/admin/add-employee`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                },
                body: JSON.stringify({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    position: finalPosition,
                }),
            });

            if (response.ok) {
                setSuccess(true);
                setFormData({
                    firstName: "",
                    lastName: "",
                    phone: "",
                    email: "",
                    position: "",
                });
                setCustomPosition("");
                if (onSuccess) onSuccess();
                setTimeout(() => setSuccess(false), 2500);
            } else {
                const errorText = await response.text();
                alert("Failed to add employee.\n\n" + errorText);
            }
        } catch (error) {
            console.error("Error adding employee:", error);
            alert("Something went wrong.");
        }
    };


    return (
        <div className="add-employee-container">
            <h2 className="admin-title">
                Add New Employee
                {countRemaining > 0 && (
                    <span className="badge">{countRemaining}</span>
                )}
            </h2>

            {success && (
                <div className="success-container">
                    <div className="checkmark-circle">&#x2714;</div>
                    <p>Employee added successfully!</p>
                </div>
            )}

            {!success && (
                <div className="admin-form">

                    {/* First Name */}
                    <input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                    />

                    {/* Last Name */}
                    <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                    />

                    {/* Phone Number */}
                    <div className="input-with-status">
                        <input
                            type="text"
                            name="phone"
                            placeholder="Phone Number"
                            value={formData.phone}
                            onChange={handleChange}
                        />
                        {formData.phone && formData.confirmPhone ? (
                            <span className={phoneMatch ? "status-ok" : "status-bad"}>
                            {phoneMatch ? "✔" : "✖"}
                            </span>
                        ) : null}
                    </div>

                    {/* Confirm Phone */}
                    <div className="input-with-status">
                        <input
                            type="text"
                            name="confirmPhone"
                            placeholder="Confirm Phone Number"
                            value={formData.confirmPhone}
                            onChange={handleChange}
                        />
                        {formData.phone && formData.confirmPhone ? (
                            <span className={phoneMatch ? "status-ok" : "status-bad"}>
                            {phoneMatch ? "✔" : "✖"}
                            </span>
                        ) : null}
                    </div>

                    {/* Email */}
                    <div className="input-with-status">
                        <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={formData.email}
                            onChange={handleChange}
                        />
                        {formData.email && formData.confirmEmail ? (
                            <span className={emailMatch ? "status-ok" : "status-bad"}>
                            {emailMatch ? "✔" : "✖"}
                            </span>
                        ) : null}
                    </div>

                    {/* Confirm Email */}
                    <div className="input-with-status">
                        <input
                            type="email"
                            name="confirmEmail"
                            placeholder="Confirm Email Address"
                            value={formData.confirmEmail}
                            onChange={handleChange}
                        />
                        {formData.email && formData.confirmEmail ? (
                            <span className={emailMatch ? "status-ok" : "status-bad"}>
                            {emailMatch ? "✔" : "✖"}
                            </span>
                        ) : null}
                    </div>
                    <select
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                    >
                        <option value="">Select Position</option>
                        {positions.map((pos, idx) => (
                            <option key={idx} value={pos}>{pos}</option>
                        ))}
                    </select>
                    {formData.position === "Other (enter manually)" && (
                        <input
                            type="text"
                            placeholder="Enter custom position"
                            value={customPosition}
                            onChange={(e) => setCustomPosition(e.target.value)}
                        />
                    )}


                    {/* ** Employee cannot be added unless:**
	                    •	First name is filled
	                      •	Last name is filled
	                    •	Phone & confirm phone match
	                    •	Email & confirm email match */}

                    <button
                        onClick={handleAddEmployee}
                        disabled={
                            !formData.firstName ||
                            !formData.lastName ||
                            !formData.phone ||
                            !formData.confirmPhone ||
                            !formData.email ||
                            !formData.confirmEmail ||
                            formData.phone !== formData.confirmPhone ||
                            formData.email !== formData.confirmEmail ||
                            !emailValid
                        }
                        className={`
        ${(
                            !formData.firstName ||
                            !formData.lastName ||
                            !formData.phone ||
                            !formData.confirmPhone ||
                            !formData.email ||
                            !formData.confirmEmail ||
                            formData.phone !== formData.confirmPhone ||
                            formData.email !== formData.confirmEmail ||
                            !emailValid
                        ) ? "btn-disabled" : ""}
    `}
                    >
                        Add Employee
                    </button>
                </div>
            )}
        </div>
    );
};

export default AddEmployee;